import { z } from 'zod/v4';
import {
  IOnDemandBookableServiceOption,
  IOnDemandAvailabilityRequest,
  IOnDemandConnectionOption,
  IOnDemandAvailabilityResponse,
  IOnDemandBookingRequest,
  IOnDemandBookingResponse,
  IOnDemandCancellationRequest,
  IOnDemandCancellationResponse,
} from './on-demand-booking.interfaces';

// ─── Bookable Service Option ─────────────────────────────────────────────────

export const ON_DEMAND_BOOKABLE_SERVICE_OPTION_SCHEMA: z.ZodType<IOnDemandBookableServiceOption> =
  z.object({
    label: z.string(),
    mobilityUserField: z.string(),
    maxCapacity: z.number(),
  });

// ─── Availability ───────────────────────────────────────────────────────────

export const ON_DEMAND_AVAILABILITY_REQUEST_SCHEMA: z.ZodType<IOnDemandAvailabilityRequest> =
  z.object({
    operatorUrl: z.string(),
    pickUpLat: z.number(),
    pickUpLng: z.number(),
    pickUpName: z.string().optional(),
    setDownLat: z.number(),
    setDownLng: z.number(),
    setDownName: z.string().optional(),
    earliestDeparture: z.string().optional(),
    latestArrival: z.string().optional(),
    passengerCount: z.number(),
    extrasPerPassenger: z.array(z.array(z.string())).optional(),
  });

export const ON_DEMAND_CONNECTION_OPTION_SCHEMA: z.ZodType<IOnDemandConnectionOption> =
  z.object({
    publicTransportXml: z.string(),
    departureTimetabled: z.string(),
    arrivalTimetabled: z.string(),
    lineName: z.string().optional(),
    bookableServiceOptions: z.array(ON_DEMAND_BOOKABLE_SERVICE_OPTION_SCHEMA),
    price: z.number().optional(),
  });

export const ON_DEMAND_AVAILABILITY_RESPONSE_SCHEMA: z.ZodType<IOnDemandAvailabilityResponse> =
  z.object({
    isAvailable: z.boolean(),
    connectionOptions: z.array(ON_DEMAND_CONNECTION_OPTION_SCHEMA),
    rejectionReason: z.string().optional(),
  });

// ─── Booking / Reservation ──────────────────────────────────────────────────

export const ON_DEMAND_BOOKING_REQUEST_SCHEMA: z.ZodType<IOnDemandBookingRequest> =
  z.object({
    operatorUrl: z.string(),
    publicTransportXml: z.string(),
    earliestDeparture: z.string(),
    latestArrival: z.string().optional(),
    passengerCount: z.number(),
    isReservation: z.boolean(),
    extrasPerPassenger: z.array(z.array(z.string())).optional(),
    systemRef: z.string().optional(),
    bookingCode: z.string().optional(),
    customerName: z.string(),
    customerEmail: z.string(),
    customerPhone: z.string().optional(),
  });

export const ON_DEMAND_BOOKING_RESPONSE_SCHEMA: z.ZodType<IOnDemandBookingResponse> =
  z.object({
    bookingRef: z.string(),
    systemRef: z.string(),
    reservedUntil: z.string().optional(),
    changeableUntil: z.string().optional(),
    cancellableUntil: z.string().optional(),
    confirmedDeparture: z.string().optional(),
    confirmedArrival: z.string().optional(),
  });

// ─── Cancellation ───────────────────────────────────────────────────────────

export const ON_DEMAND_CANCELLATION_REQUEST_SCHEMA: z.ZodType<IOnDemandCancellationRequest> =
  z.object({
    operatorUrl: z.url(),
    bookingRef: z.string().min(1),
    systemRef: z.string().min(1),
    customerName: z.string().min(1).optional(),
    customerEmail: z.email().optional(),
  });

export const ON_DEMAND_CANCELLATION_RESPONSE_SCHEMA: z.ZodType<IOnDemandCancellationResponse> =
  z.object({
    errorMessage: z.string().optional(),
  });
